export type EducationBadge = { short: string; full: string; color: string };

/** The palette the old hardcoded maps used, now dealt out by id so any new
 *  year/section gets a stable colour automatically. */
const PALETTE = [
	'#C7C5F4',
	'#A7A2F1',
	'#5AC0FC',
	'#6C63FF',
	'#FF8532',
	'#FFD037',
	'#63D3A6',
];

const ORDINALS: Record<string, string> = {
	first: '1st',
	second: '2nd',
	third: '3rd',
	fourth: '4th',
	fifth: '5th',
	sixth: '6th',
	seventh: '7th',
	eighth: '8th',
	ninth: '9th',
	// Arabic ordinals, with and without the definite article.
	اول: '1st',
	أول: '1st',
	الاول: '1st',
	الأول: '1st',
	الاولى: '1st',
	الأولى: '1st',
	ثاني: '2nd',
	ثانى: '2nd',
	الثاني: '2nd',
	الثانى: '2nd',
	الثانية: '2nd',
	ثالث: '3rd',
	الثالث: '3rd',
	الثالثة: '3rd',
	رابع: '4th',
	الرابع: '4th',
	الرابعة: '4th',
	خامس: '5th',
	الخامس: '5th',
	الخامسة: '5th',
	سادس: '6th',
	السادس: '6th',
	السادسة: '6th',
};

const STAGES: Record<string, string> = {
	secondary: 'Sec',
	preparatory: 'Prep',
	primary: 'Prim',
	baccalaureate: 'Bacc',
	kindergarten: 'KG',
	// Arabic stages, spelling variants included.
	ثانوي: 'Sec',
	ثانوى: 'Sec',
	الثانوي: 'Sec',
	الثانوى: 'Sec',
	ثانوية: 'Sec',
	الثانوية: 'Sec',
	اعدادي: 'Prep',
	اعدادى: 'Prep',
	إعدادي: 'Prep',
	الاعدادي: 'Prep',
	الاعدادى: 'Prep',
	الإعدادي: 'Prep',
	اعدادية: 'Prep',
	الاعدادية: 'Prep',
	الإعدادية: 'Prep',
	ابتدائي: 'Prim',
	ابتدائى: 'Prim',
	الابتدائي: 'Prim',
	الإبتدائي: 'Prim',
	ابتدائية: 'Prim',
	الابتدائية: 'Prim',
	بكالوريا: 'Bacc',
	البكالوريا: 'Bacc',
	روضة: 'KG',
	الروضة: 'KG',
};

const WORDS: Record<string, string> = {
	mathematics: 'Math',
	science: 'Sci',
	literature: 'Lit',
	arabic: 'Ar',
	english: 'En',
	french: 'Fr',
	german: 'De',
};

/** Letters and digits only — names like "Science (علمي)" must not leak
 *  brackets or dashes into the badge. */
const cleanWord = (word: string) => word.replace(/[^\w؀-ۿ]/g, '');

/**
 * Compact badge for an education year/section derived from its real name, so
 * admin-created entries render without any hardcoded lookup. Handles the
 * underscore/space production variants, English and Arabic ordinal+stage
 * names ("First Secondary" and "الصف الأول الثانوي" both come out 1st Sec),
 * "Grade 11" shapes, and falls back to text that always fits the pill.
 */
export const educationBadge = (edu: {
	id?: number;
	name?: string;
}): EducationBadge => {
	const full = String(edu?.name ?? '')
		.replace(/_/g, ' ')
		.trim();
	const color = PALETTE[Math.abs(edu?.id ?? 0) % PALETTE.length];

	const grade = full.match(/\bgrade\s*(\d+)\b/i);
	if (grade) return { short: `G${grade[1]}`, full, color };

	const words = full.split(/\s+/).map(cleanWord).filter(Boolean);

	let ordinal = '';
	let stage = '';
	for (const word of words) {
		const lower = word.toLowerCase();
		if (!ordinal && ORDINALS[lower]) ordinal = ORDINALS[lower];
		else if (!stage && STAGES[lower]) stage = STAGES[lower];
	}
	if (ordinal || stage) {
		return { short: [ordinal, stage].filter(Boolean).join(' '), full, color };
	}

	// A known subject word anywhere in the name wins: "Science (علمي)" → Sci.
	for (const word of words) {
		const known = WORDS[word.toLowerCase()];
		if (known) return { short: known, full, color };
	}

	// Short names fit the pill whole — Arabic sections like علمي or أدبي.
	const cleanedFull = words.join(' ');
	if (cleanedFull && cleanedFull.length <= 10) {
		return { short: cleanedFull, full, color };
	}

	// Initials only make sense for Latin names; Arabic ones show their first
	// word instead of a meaningless letter mash.
	const latinOnly = /^[A-Za-z0-9 ]+$/.test(cleanedFull);
	if (words.length > 1 && latinOnly) {
		return {
			short: words
				.slice(0, 3)
				.map((word) => word[0]?.toUpperCase() ?? '')
				.join(''),
			full,
			color,
		};
	}
	const short = (words[0] ?? '?').slice(0, 8);
	return { short: short || '?', full, color };
};

/** Tooltip-ready copy of the list with the same name normalization the
 *  badges use — raw payload names mix underscore and space variants. */
export const educationFullNames = (
	edus?: Array<{ id?: number; name?: string }>
) => (edus ?? []).map((edu) => ({ ...edu, name: educationBadge(edu).full }));

export default educationBadge;
